import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';

interface ContainerProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  fullWidth?: boolean; // For background elements that should ignore margins
  showMargins?: boolean; // Debug option to visualize margins
}

/**
 * Container component that applies responsive margins
 * Uses consistent 10% margins across all breakpoints
 */
export const Container: React.FC<ContainerProps> = ({ 
  children, 
  className = '', 
  style = {},
  fullWidth = false,
  showMargins = false
}) => {
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  // Update viewport width when resizing
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate margin width - consistently 10% of viewport width
  const calculateMarginWidth = () => {
    // Use the max content width of 1728px as a constraint
    const MAX_CONTENT_WIDTH = 1728;
    
    // If viewport is wider than max width, calculate based on max width
    if (viewportWidth > MAX_CONTENT_WIDTH) {
      // Return 10% of max width
      return MAX_CONTENT_WIDTH * 0.1;
    } else {
      // Otherwise use 10% of viewport width
      return viewportWidth * 0.1;
    }
  };

  // For full width elements (like backgrounds), don't apply margin constraints
  if (fullWidth) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  // For regular content, apply the responsive margin system
  return (
    <div 
      className={`
        w-full
        ${className}
        ${showMargins ? 'relative margin-debug' : ''}
      `}
      style={{
        ...style,
        paddingLeft: `${calculateMarginWidth()}px`,
        paddingRight: `${calculateMarginWidth()}px`,
        maxWidth: '1728px',
        margin: viewportWidth > 1728 ? '0 auto' : undefined,
      }}
    >
      {children}
    </div>
  );
};

export default Container; 